import { Component, OnInit, Inject, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { removeBlankStringCells } from '../../shared/utils/excel.utils';
import {
  SalesReportsService,
  SalesReportItem,
  SalesReportParams,
} from '../../core/services/sales-reports.service';

type GroupMode = 'KU' | 'P' | 'A' | 'K';

interface HeaderColumn {
  label: string;
  key: string;
  type: 'string' | 'number';
}

interface DisplayItem extends SalesReportItem {
  timpris: number | null;
}

interface ChartDataItem {
  name: string;
  value: number;
}

function applyWorksheetFormats(
  worksheet: XLSX.WorkSheet,
  headers: HeaderColumn[],
  rowCount: number
): void {
  for (let row = 1; row <= rowCount; row++) {
    headers.forEach((header, col) => {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      const cell = worksheet[cellAddress];
      if (!cell || cell.v === '' || cell.v === null || cell.v === undefined) {
        return;
      }

      if (header.type === 'number') {
        const numValue = typeof cell.v === 'number' ? cell.v : Number(cell.v);
        if (!Number.isNaN(numValue)) {
          cell.t = 'n';
          cell.v = numValue;
          cell.z = Number.isInteger(numValue) ? '0' : '0.00';
        }
      }
    });
  }
}

@Component({
  selector: 'app-ng-sales-reports',
  template: require('./ng-sales-reports.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-sales-reports.component.scss').default],
  standalone: false,
})
export class NgSalesReportsComponent implements OnInit {
  // Date range
  dateFrom: moment.Moment = moment().startOf('month');
  dateTo: moment.Moment = moment().endOf('month');

  // Modes
  groupMode: GroupMode = 'P';
  subTotal = false;

  // Data
  invoiceData: DisplayItem[] = [];
  headers: HeaderColumn[] = [];

  // Chart data
  hoursChartData: ChartDataItem[] = [];
  revenueChartData: ChartDataItem[] = [];

  // Sorting
  private sortAscending = true;

  private destroyRef = inject(DestroyRef);

  constructor(@Inject(SalesReportsService) private salesReportsService: SalesReportsService) {}

  ngOnInit(): void {
    // Initial state ready, user clicks "Skapa" to fetch
  }

  updatePeriod(direction: number): void {
    if (direction === -1) {
      this.dateFrom = moment(this.dateFrom).subtract(1, 'month').startOf('month');
      this.dateTo = moment(this.dateFrom).endOf('month');
    } else {
      this.dateFrom = moment(this.dateFrom).add(1, 'month').startOf('month');
      this.dateTo = moment(this.dateFrom).endOf('month');
    }
  }

  fetchReport(): void {
    this.updateHeaders();

    const params: SalesReportParams = {
      from: this.dateFrom.format('YYYY-MM-DD'),
      to: this.dateTo.format('YYYY-MM-DD'),
      type: this.groupMode,
      per: 'A',
      sum: 'A',
      subTotal: this.subTotal,
      custMode: { external: true, internal: false },
    };

    this.salesReportsService.getSalesReport(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.invoiceData = this.processData(data);
      this.buildChartData();
    });
  }

  private updateHeaders(): void {
    const baseHeaders: HeaderColumn[] = [{ label: 'Kund', key: 'customername', type: 'string' }];

    switch (this.groupMode) {
      case 'KU':
        this.headers = [
          ...baseHeaders,
          { label: 'Timmar', key: 'sum', type: 'number' },
          { label: 'Timpris', key: 'timpris', type: 'number' },
          { label: 'Summa', key: 'pris', type: 'number' },
        ];
        break;
      case 'P':
        this.headers = [
          ...baseHeaders,
          { label: 'Projekt', key: 'project_name', type: 'string' },
          { label: 'Ext projekt', key: 'external_project_name', type: 'string' },
          { label: 'Timmar', key: 'sum', type: 'number' },
          { label: 'Timpris', key: 'timpris', type: 'number' },
          { label: 'Summa', key: 'pris', type: 'number' },
        ];
        break;
      case 'A':
        this.headers = [
          ...baseHeaders,
          { label: 'Projekt', key: 'project_name', type: 'string' },
          { label: 'Ext projekt', key: 'external_project_name', type: 'string' },
          { label: 'Aktivitet', key: 'activity_name', type: 'string' },
          { label: 'Ext aktivitet', key: 'external_activity_name', type: 'string' },
          { label: 'Timmar', key: 'sum', type: 'number' },
          { label: 'Timpris', key: 'timpris', type: 'number' },
          { label: 'Summa', key: 'pris', type: 'number' },
        ];
        break;
      case 'K':
        this.headers = [
          ...baseHeaders,
          { label: 'Projekt', key: 'project_name', type: 'string' },
          { label: 'Ext projekt', key: 'external_project_name', type: 'string' },
          { label: 'Aktivitet', key: 'activity_name', type: 'string' },
          { label: 'Ext aktivitet', key: 'external_activity_name', type: 'string' },
          { label: 'Namn', key: 'name', type: 'string' },
          { label: 'Timmar', key: 'sum', type: 'number' },
          { label: 'Timpris', key: 'timpris', type: 'number' },
          { label: 'Summa', key: 'pris', type: 'number' },
        ];
        break;
    }
  }

  private processData(data: SalesReportItem[]): DisplayItem[] {
    return data.map((item) => {
      const timpris = item.sum > 0 ? Math.round((item.pris / item.sum) * 100) / 100 : null;
      return {
        ...item,
        customername: item.customername ?? 'Tot',
        timpris,
      };
    });
  }

  private buildChartData(): void {
    this.hoursChartData = [];
    this.revenueChartData = [];

    for (const item of this.invoiceData) {
      if (item.customername === 'Tot') continue;

      const name = this.getChartLabel(item);
      if (!name) continue;

      this.hoursChartData.push({ name, value: item.sum });
      this.revenueChartData.push({ name, value: item.pris });
    }
  }

  private getChartLabel(item: DisplayItem): string | null {
    switch (this.groupMode) {
      case 'KU':
        return item.customername;
      case 'P':
        return item.project_name ?? null;
      case 'A':
        return item.activity_name ?? null;
      default:
        return null;
    }
  }

  sortBy(index: number): void {
    const header = this.headers[index];
    const key = header.key as keyof DisplayItem;

    this.invoiceData.sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison =
        header.type === 'number'
          ? (aVal as number) - (bVal as number)
          : String(aVal).localeCompare(String(bVal), 'sv');

      return this.sortAscending ? comparison : -comparison;
    });

    this.sortAscending = !this.sortAscending;
    this.invoiceData = [...this.invoiceData]; // Trigger change detection
  }

  getCellValue(item: DisplayItem, header: HeaderColumn): string | number | null {
    const value = item[header.key as keyof DisplayItem];
    if (value === null || value === undefined) return null;
    return value;
  }

  exportToExcel(): void {
    const exportData = this.invoiceData.map((item) => {
      const row: Record<string, string | number> = {};
      for (const header of this.headers) {
        row[header.label] = item[header.key as keyof DisplayItem] ?? '';
      }
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    removeBlankStringCells(worksheet);
    applyWorksheetFormats(worksheet, this.headers, exportData.length);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Säljrapport');
    XLSX.writeFile(workbook, 'saljRapport.xlsx');
  }

  trackByIndex(index: number): number {
    return index;
  }
}
